import React from "react";
import {DataGrid, GridColDef, GridActionsCellItem, GridToolbarContainer, GridRenderCellParams} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {Button, Chip} from "@mui/material";
import {Box} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import IServiceOrder from "../../../types/service-order.type";
import AddIcon from "@mui/icons-material/Add";
import IAllocatedResource from "../../../types/allocated-resource.type";
import AllocatedResourceModal from "./AllocatedResourceModal";
import IUser from "../../../types/user.type";
import showNotification from "../../utils/Notify";
import ConfirmationModal from "../../utils/ConfirmationModal";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { v4 as uuidv4 } from 'uuid';
import {WorkPositionNames} from "../../../types/enumeration/WorkPositionNames";
import {WorkPosition} from "../../../types/enumeration/WorkPosition";

interface Props {
  serviceOrder: IServiceOrder;
  setServiceOrder: (updatedServiceOrder: IServiceOrder | ((prev: IServiceOrder) => IServiceOrder)) => void;
  users: IUser[],
  readOnly?: boolean;
}

const AllocatedResourcesList: React.FC<Props> = ({serviceOrder, setServiceOrder, users, readOnly = false}) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
  const [selectedAllocatedResource, setSelectedAllocatedResource] = React.useState<IAllocatedResource>();

  const handleConfirmationModalClose = () => {
    setSelectedAllocatedResource(undefined);
    setConfirmationModalOpen(false);
  }

  const handleDeleteClick = (allocatedResource: IAllocatedResource) => () => {
    setSelectedAllocatedResource(allocatedResource);
    setConfirmationModalOpen(true);
  };

  const onConfirm = () => {

    if (!selectedAllocatedResource) return;

    const isUsedInWorkScopes = serviceOrder.workScopes.some(ws =>
      ws.resources.some(resource => resource.id === selectedAllocatedResource?.worker?.id)
    );

    const isUsedInBudgets = serviceOrder.budgets.some(budget =>
      budget.resources.some(resource => resource.id === selectedAllocatedResource?.worker?.id)
    );

    if (isUsedInWorkScopes || isUsedInBudgets) {
      showNotification({type: 'error', message: 'No se puede eliminar el recurso asignado porque está en uso en una Orden de Trabajo o Presupuesto'});
      return;
    }

    setServiceOrder(prev => ({
      ...prev,
      allocatedResources: prev.allocatedResources.filter(ws => ws.id !== selectedAllocatedResource?.id),
    }));

    setSelectedAllocatedResource(undefined);
    setConfirmationModalOpen(false);
  };

  const handleModalClose = () => {
    setSelectedAllocatedResource(undefined);
    setModalOpen(false);
  }

  const handleAddClick = () => {
    setSelectedAllocatedResource(undefined);
    setModalOpen(true);
  };

  const handleEditClick = (allocatedResource: IAllocatedResource) => () => {
    setSelectedAllocatedResource(allocatedResource);
    setModalOpen(true);
  };

  const onSubmit = (allocatedResource: IAllocatedResource) => {
    const id =
      allocatedResource.id
      ?? globalThis.crypto?.randomUUID?.()
      ?? uuidv4();

    const newAllocatedResource = {
      ...allocatedResource,
      id,
    };

    const isAlreadyAssigned = serviceOrder.allocatedResources.some(
      (resource: IAllocatedResource) =>
        resource.worker?.id === newAllocatedResource.worker?.id &&
        resource.id !== newAllocatedResource.id
    );

    if (isAlreadyAssigned) {
      showNotification({
        type: 'error',
        message: 'El recurso ya esta asignado a la Orden de Trabajo.'
      });
      return;
    }

    setServiceOrder(prev => ({
      ...prev,
      allocatedResources: prev.allocatedResources.some(resource => resource.id === newAllocatedResource.id)
        ? prev.allocatedResources.map(resource =>
          resource.id === newAllocatedResource.id ? newAllocatedResource : resource
        )
        : [...prev.allocatedResources, newAllocatedResource],
    }));

    setSelectedAllocatedResource(undefined);
    setModalOpen(false);
  };

  const columns: GridColDef<IAllocatedResource>[] = [
    {
      field: 'worker',
      headerName: 'Recurso Asignado',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span>{`${params.row.worker.lastName + ', ' + params.row.worker.name}`}</span>;
      }
    },
    {
      field: 'workPosition',
      headerName: 'Puesto',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{WorkPositionNames[WorkPosition[params.row.workPosition as keyof typeof WorkPosition]]}</span>;
      }
    },
    {
      field: 'confirmed',
      headerName: 'Recepción Confirmada',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={`${params.row.confirmed  ? 'success' : 'error'}`}
                     label={`${params.row.confirmed  ? 'SI' : 'NO'}`}
                     sx={{width: 40, height: 18, fontWeight: 'bold', fontSize: 10}}/>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      flex: 1,
      cellClassName: 'actions',
      getActions: (params) => {
        if (readOnly){
          return [
            <GridActionsCellItem
              icon={<VisibilityIcon/>}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(params.row)}
              color="inherit"
            />
          ];
        } else {
          return [
            <GridActionsCellItem
              icon={<EditIcon/>}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(params.row)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={<DeleteForeverIcon/>}
              label="Delete"
              className="textPrimary"
              onClick={handleDeleteClick(params.row)}
              color="inherit"
            />
          ];
        }
      },
    },
  ];

  function EditToolbar() {
    return (
      <GridToolbarContainer sx={{paddingBottom: 1}}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          endIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={readOnly}
        >
          Asignar Recurso
        </Button>
      </GridToolbarContainer>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 384, typography: 'body1' }} mt={1}>

      <ConfirmationModal
        open={confirmationModalOpen}
        onConfirm={onConfirm}
        onCancel={handleConfirmationModalClose} />

      <AllocatedResourceModal
        isOpen={modalOpen}
        onSubmit={onSubmit}
        onClose={handleModalClose}
        allocatedResource={selectedAllocatedResource}
        users={users}
        readOnly={readOnly}
      />

      <CssVarsProvider>
        <DataGrid
          slots={{toolbar: EditToolbar}}
          rows={serviceOrder.allocatedResources}
          columns={columns}
          rowHeight={25}
          columnHeaderHeight={30}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10]}
          localeText={{ noRowsLabel: "No Hay Recursos Asignados" }}
          disableRowSelectionOnClick
        />

      </CssVarsProvider>
    </Box>
  );
};

export default AllocatedResourcesList;
